import React from "react";
import { Link } from "react-router-dom";

/**
 * ANATRADEX Academy — Second batch of extra tracks (Macro/Fed & Cycles, Fiscalité FR).
 * Same shape as CHARTISTE_ARTICLES: {slug, track, level, minutes, order, title, summary, content}.
 */

// -----------------------------------------------------------------------------
// Track: Macro / Fed / Cycles
// -----------------------------------------------------------------------------
export const MACRO_ARTICLES = [
  {
    slug: "comprendre-la-fed-en-15-minutes",
    track: "macro",
    level: "Débutant",
    minutes: 6,
    order: 1,
    title: "Comprendre la Fed en 15 minutes",
    summary: "FOMC, dot plot, taux directeurs, QT/QE. Le vocabulaire minimal pour trader les news US.",
    content: (
      <>
        <p>La Réserve fédérale américaine (Federal Reserve, « la Fed ») est de loin l&apos;acteur macro le plus important au monde. Ses décisions bougent tous les marchés — actions, obligations, forex, crypto, matières premières.</p>
        <h3>Structure de la Fed</h3>
        <ul>
          <li><b>FOMC</b> (Federal Open Market Committee) : le comité qui décide de la politique monétaire. 12 membres votants (7 Board + 5 régionaux tournants).</li>
          <li><b>8 réunions par an</b> — dates publiées à l&apos;avance. Chacune produit un <em>statement</em> + une conférence de presse du président.</li>
          <li><b>Le président</b> actuel est Jerome Powell (mandat jusqu&apos;en 2026). Son ton — <em>hawkish</em> (dur, restrictif) ou <em>dovish</em> (accommodant) — fait bouger les marchés autant que la décision elle-même.</li>
        </ul>
        <h3>Les 3 outils de la Fed</h3>
        <ol>
          <li><b>Taux directeur (Fed Funds Rate)</b> : le taux auquel les banques se prêtent au jour le jour. C&apos;est le levier principal. Hausse = économie freine = USD monte = actions baissent (généralement).</li>
          <li><b>QE / QT</b> (Quantitative Easing / Tightening) : la Fed rachète (QE) ou vend (QT) des obligations pour injecter ou retirer de la liquidité. QE = liquidité = bull equity/crypto. QT = drain = risk-off.</li>
          <li><b>Guidance forward</b> : les mots. « We remain data-dependent » vs « we are prepared to act decisively » changent tout.</li>
        </ol>
        <h3>Le Dot Plot</h3>
        <p>Publié 4× par an, c&apos;est un graphique où chaque point = anticipation de taux d&apos;un membre du FOMC pour les années à venir. La <b>médiane</b> est ce que le marché regarde. Un dot plot qui se déplace vers le haut de 25 bps = shock hawkish.</p>
        <h3>Comment trader la Fed</h3>
        <ol>
          <li>Vérifiez le <em>consensus</em> (Reuters, Bloomberg) avant l&apos;annonce. Le prix est déjà « in ».</li>
          <li>La vraie action arrive dans les <b>2-30 min</b> suivant le communiqué + press conf.</li>
          <li>Ne prenez <b>jamais</b> de position 15 min avant le communiqué — spread × 5.</li>
          <li>Cherchez les <em>surprises</em> : décision différente du consensus, ou même décision mais ton différent.</li>
        </ol>
        <h3>Corrélations Fed-actifs</h3>
        <ul>
          <li><b>Hawkish surprise</b> : USD ↑, gold ↓, actions ↓, crypto ↓, US10Y yield ↑</li>
          <li><b>Dovish surprise</b> : miroir parfait</li>
          <li><b>Statu quo attendu</b> : la conférence de presse fait tout — écoutez le ton</li>
        </ul>
        <p>ANATRADEX marque automatiquement les jours FOMC dans le panneau <Link to="/app/dashboard" className="text-accent-blue underline">Events</Link> avec un <em>tail_risk_score</em> boosté — protégez-vous en réduisant la taille ces jours-là.</p>
      </>
    ),
  },
  {
    slug: "cycles-economiques-et-rotation-sectorielle",
    track: "macro",
    level: "Semi-pro",
    minutes: 7,
    order: 2,
    title: "Cycles économiques et rotation sectorielle",
    summary: "Early / Mid / Late / Recession. Chaque phase favorise des secteurs et des paires FX différents.",
    content: (
      <>
        <p>L&apos;économie ne monte jamais en ligne droite. Elle oscille en 4 phases identifiables. Reconnaître la phase actuelle = savoir quels actifs surperforment.</p>
        <h3>Les 4 phases du cycle</h3>
        <ol>
          <li><b>Early cycle (récupération)</b> — après une récession. Taux bas, PMI en hausse, chômage en baisse. Bull actions <em>agressif</em>, secteurs cycliques (tech, industrie, consumer disc).</li>
          <li><b>Mid cycle (expansion)</b> — croissance solide, inflation contenue. Bull actions modéré, corporate margins pic. Secteurs : tech, financials, materials.</li>
          <li><b>Late cycle (surchauffe)</b> — inflation qui monte, la Fed durcit. Rotation vers énergie, matériaux, gold. Le S&P performe encore, la vol monte.</li>
          <li><b>Recession</b> — contraction. Défensifs (utilities, staples, healthcare), long duration bonds, cash.</li>
        </ol>
        <h3>Comment identifier la phase</h3>
        <ul>
          <li><b>Yield curve (2s10s spread)</b> : positive raide = early, plate = late, inversée = récession dans 12-18 mois.</li>
          <li><b>ISM PMI</b> : {">"} 50 = expansion. {"<"} 45 = récession.</li>
          <li><b>Chômage US</b> : bas et stable = mid/late. Qui remonte = début récession.</li>
          <li><b>Inflation core</b> : {"<"} 2% = early. {">"} 4% = late.</li>
          <li><b>Fed stance</b> : accommodante = early, neutre = mid, restrictive = late/récession.</li>
        </ul>
        <h3>Rotation FX par phase</h3>
        <ul>
          <li><b>Early</b> : USD faible (Fed accommodante). Long EM currencies (BRL, MXN, ZAR), long AUD (commodities boom).</li>
          <li><b>Mid</b> : USD stable. Trades directionnels sur fundamentals pays par pays.</li>
          <li><b>Late</b> : USD fort (Fed hawkish + safe haven). Short EM, long JPY/CHF/USD.</li>
          <li><b>Recession</b> : JPY et CHF explosent (safe havens absolus). Gold + Bitcoin (parfois).</li>
        </ul>
        <h3>Où sommes-nous en 2026 ?</h3>
        <p>Configuration hybride late-cycle. Fed en pause ou légère baisse, yield curve à peine inversée, chômage stable à 4%, PMI 52, inflation core 2,8%. Historiquement, cette config précède une phase de vol élevée sur 6-12 mois.</p>
        <p><b>Implication trading :</b> réduire l&apos;exposition equity beta, augmenter l&apos;exposition gold / CHF / cash. Le régime ANATRADEX va probablement flip risk-off en 2026 H1.</p>
      </>
    ),
  },
  {
    slug: "les-indicateurs-macro-que-tout-trader-doit-suivre",
    track: "macro",
    level: "Débutant",
    minutes: 6,
    order: 3,
    title: "10 indicateurs macro à surveiller religieusement",
    summary: "NFP, CPI, ISM, PMI, chômage, retail sales, etc. Le calendrier économique décodé.",
    content: (
      <>
        <p>Les indicateurs macro publiés mensuellement sont les événements les plus prévisibles ET les plus rentables — car tous les marchés y réagissent en même temps.</p>
        <h3>Les 10 indicateurs par ordre d&apos;importance</h3>
        <ol>
          <li><b>NFP (Non-Farm Payrolls)</b> — 1er vendredi du mois, 8h30 ET. Nombre d&apos;emplois créés hors agriculture aux US. Surprise de +100k = USD ↑↑. Surprise de -100k = USD ↓↓.</li>
          <li><b>CPI (Consumer Price Index)</b> — inflation. Publié milieu du mois. La <em>core CPI</em> (hors énergie/alimentation) importe plus que le headline. Surprise = mouvement 50-100 pips FX en 5 min.</li>
          <li><b>FOMC statement + press conf</b> — 8× par an. Voir article dédié.</li>
          <li><b>PCE (Personal Consumption Expenditures)</b> — la mesure d&apos;inflation <em>préférée</em> de la Fed. Moins volatile que le CPI.</li>
          <li><b>ISM Manufacturing PMI</b> — 1er jour ouvré du mois. Sentiment des directeurs d&apos;achat. {">"} 50 = expansion.</li>
          <li><b>ISM Services PMI</b> — 3e jour ouvré. Plus important que Manufacturing car les services = 70% du PIB US.</li>
          <li><b>Retail Sales</b> — santé du consommateur. Baisse consécutive de 3 mois = signal récession.</li>
          <li><b>Chômage hebdo (Initial Claims)</b> — jeudi 8h30 ET. Meilleur indicateur avancé du marché du travail.</li>
          <li><b>GDP</b> — trimestriel. Confirme (rétroactivement) où on est dans le cycle.</li>
          <li><b>Consumer confidence (Conf. Board / Michigan)</b> — expectations forward.</li>
        </ol>
        <h3>Comment se préparer</h3>
        <p>Chaque dimanche soir, listez les publications de la semaine à venir depuis Forexfactory, Investing ou le panneau <em>Events</em> ANATRADEX. Marquez en rouge : NFP, CPI, FOMC. En jaune : ISM, retail sales, PCE.</p>
        <h3>Les 3 règles absolues</h3>
        <ol>
          <li>Aucune entrée nouvelle 15 min avant une publication rouge.</li>
          <li>Si vous êtes déjà en position sur un actif corrélé (USD paires + NFP), réduisez la taille de 50%.</li>
          <li>Attendez 5-10 min après la publication pour entrer — le spread revient à la normale, la vraie direction s&apos;installe.</li>
        </ol>
        <p>Chaque publication est un événement à <b>haut edge statistique</b> pour les traders préparés. Ne les subissez pas — profitez-en.</p>
      </>
    ),
  },
  {
    slug: "yield-curve-et-recession",
    track: "macro",
    level: "Pro",
    minutes: 7,
    order: 4,
    title: "Yield curve : le meilleur signal de récession du monde",
    summary: "2s10s inversion. Historique 100% depuis 1955. Comment la lire et la trader.",
    content: (
      <>
        <p>La courbe des taux (yield curve) est le graphique le plus surveillé de la finance. L&apos;inversion 2s10s a prédit <b>chaque récession US depuis 1955</b>, sans jamais donner de faux signal (100% de précision, 7/7).</p>
        <h3>Qu&apos;est-ce que le 2s10s ?</h3>
        <p>C&apos;est la différence entre le rendement du bon du Trésor US 10 ans et 2 ans. <br />
        <code>2s10s = US10Y - US2Y</code></p>
        <p>Positif (courbe normale) : les investisseurs demandent plus pour prêter longtemps que court. C&apos;est la config normale.</p>
        <p>Négatif (inversion) : le marché anticipe des baisses de taux futures = récession attendue.</p>
        <h3>Le timing historique</h3>
        <ul>
          <li>Inversion → récession : délai moyen <b>12-24 mois</b>. Jamais moins de 6 mois, jamais plus de 30.</li>
          <li>Profondeur d&apos;inversion : pas prédictive de la sévérité, juste de l&apos;occurrence.</li>
          <li>La courbe <em>désinverse</em> juste avant / pendant la récession — c&apos;est le signal ultime.</li>
        </ul>
        <h3>Comment la trader</h3>
        <ol>
          <li><b>Signal d&apos;inversion (2s10s {"<"} 0)</b> : commencer à réduire l&apos;exposition equity beta. Augmenter gold + JPY + cash.</li>
          <li><b>Signal de désinversion + chômage qui remonte</b> : c&apos;est LE moment. Fermez toutes les positions equity long, allez cash 60%.</li>
          <li><b>Pendant la récession</b> : long duration bonds (TLT), long JPY, long CHF, gold. Attendez que la Fed pivote (baisse de taux).</li>
          <li><b>Post-Fed pivot</b> : rentrez agressivement sur les cyclicals US (tech, small caps). C&apos;est le trade de la décennie.</li>
        </ol>
        <h3>Autres spreads à surveiller</h3>
        <ul>
          <li><b>3m10s</b> : préféré par la Fed elle-même comme indicateur.</li>
          <li><b>2s5s</b> : plus sensible aux anticipations de politique monétaire court terme.</li>
          <li><b>Credit spreads (HY - IG)</b> : quand ils explosent, la récession est là.</li>
        </ul>
        <h3>Le débat 2020-2026</h3>
        <p>L&apos;inversion 2022-2024 (la plus longue depuis 1980) a fait douter certains sur la fiabilité du signal (« this time is different »). Ce sont les mêmes phrases prononcées en 2000 et 2007. La désinversion 2025 a coïncidé avec le début du ralentissement — le signal a tenu.</p>
        <p><b>En bref :</b> ignorez le 2s10s à vos risques et périls. C&apos;est la métrique unique la plus puissante pour timer les cycles.</p>
      </>
    ),
  },
  {
    slug: "dxy-et-son-impact-multi-actifs",
    track: "macro",
    level: "Semi-pro",
    minutes: 5,
    order: 5,
    title: "DXY (Dollar Index) : le trade macro central",
    summary: "Un seul chart à regarder pour comprendre 60% des flows FX/commodities/crypto.",
    content: (
      <>
        <p>Le <b>Dollar Index (DXY)</b> mesure la valeur du USD contre un panier de 6 devises majeures (EUR 58%, JPY 14%, GBP 12%, CAD 9%, SEK 4%, CHF 4%). C&apos;est le baromètre le plus utilisé de la force du dollar.</p>
        <h3>Pourquoi le DXY est le KING trade</h3>
        <p>Le dollar est la devise de réserve mondiale. 60% des exports mondiaux sont libellés en USD. 90% des transactions FX passent par le USD. Résultat : <b>tout est corrélé au DXY, directement ou indirectement</b>.</p>
        <h3>Corrélations principales</h3>
        <ul>
          <li><b>DXY ↑ → Gold ↓</b> (corrélation -0,80 à -0,90 sur 10 ans)</li>
          <li><b>DXY ↑ → Commodities ↓</b> (or, pétrole, cuivre, agri — tout est priced en USD)</li>
          <li><b>DXY ↑ → EM currencies ↓↓</b> (BRL, MXN, TRY, ZAR)</li>
          <li><b>DXY ↑ → BTC ↓</b> (depuis 2020, corrélation ~ -0,50)</li>
          <li><b>DXY ↑ → US actions ↓</b> (léger, ~ -0,30)</li>
        </ul>
        <h3>Les drivers du DXY</h3>
        <ol>
          <li><b>Différentiel de taux Fed vs BCE/BoJ</b> — driver #1</li>
          <li><b>Flight to safety</b> — en crise, le USD monte (haven), même contre son intérêt</li>
          <li><b>Croissance relative US vs Europe/Japon</b></li>
          <li><b>Balance commerciale US</b> — déficit large = pression baissière LT</li>
        </ol>
        <h3>Niveaux techniques historiques</h3>
        <ul>
          <li><b>90-92</b> : plancher structurel de la décennie 2010-2020</li>
          <li><b>100</b> : niveau psychologique majeur</li>
          <li><b>108-115</b> : zone haussière historique (2001-2002, 2022, 2025)</li>
          <li><b>{">"} 120</b> : quasi-jamais atteint depuis 1985 — plafond structurel</li>
        </ul>
        <h3>Trade template DXY-centric</h3>
        <p>Chaque matin, la première chose à regarder = DXY. Si DXY explode {">"} 108 : shortez gold, shortez EM, restez cash sur crypto. Si DXY casse {"<"} 100 : longez gold, longez EM, allez risk-on. C&apos;est le trade macro le plus rentable de la décennie 2020-2030 si vous le tenez.</p>
        <p><Link to="/app/dashboard" className="text-accent-blue underline">Le régime ANATRADEX</Link> intègre automatiquement le DXY comme facteur dans son modèle multi-actifs.</p>
      </>
    ),
  },
];

// -----------------------------------------------------------------------------
// Track: Fiscalité du trader (FR)
// -----------------------------------------------------------------------------
export const FISCAL_ARTICLES = [
  {
    slug: "fiscalite-forex-crypto-france-2026",
    track: "fiscal",
    level: "Débutant",
    minutes: 8,
    order: 1,
    title: "Fiscalité forex + crypto en France (2026)",
    summary: "PFU 30%, BNC pro, PVCT crypto. Le guide pratique pour ne pas se planter au 2042.",
    content: (
      <>
        <p><b>Disclaimer :</b> cet article est éducatif, pas un conseil fiscal personnalisé. Consultez un expert-comptable ou fiscaliste avant de finaliser votre déclaration. Les règles évoluent — vérifiez pour l&apos;année en cours.</p>
        <h3>Trading forex/CFD — régime par défaut</h3>
        <p>En France, les plus-values sur trading forex/CFD chez un broker <b>non résident fiscal FR</b> (ex : IC Markets, Pepperstone…) sont taxées au <b>PFU (Prélèvement Forfaitaire Unique) à 30%</b> :</p>
        <ul>
          <li>12,8% impôt sur le revenu</li>
          <li>17,2% prélèvements sociaux (CSG/CRDS)</li>
          <li>= 30% flat sur la plus-value nette</li>
        </ul>
        <p>Option : barème progressif si TMI (tranche marginale d&apos;imposition) {"<"} 12,8% (peu commun). À évaluer avec un pro.</p>
        <h3>Case à cocher</h3>
        <p>Case <b>2TR</b> ou <b>3VG</b> selon le broker et le formulaire fourni. Certains brokers étrangers ne fournissent aucun IFU (Imprimé Fiscal Unique) — vous devez calculer et déclarer vous-même. <b>Gardez toutes vos statements mensuels.</b></p>
        <h3>Crypto — régime particulier</h3>
        <p>Les plus-values crypto occasionnelles pour un particulier :</p>
        <ul>
          <li><b>PFU 30%</b> sur la PV nette (sortie EUR / stablecoin fiat)</li>
          <li><b>Seuil</b> : jusqu&apos;à 305 € de cessions annuelles = exonération</li>
          <li><b>Swap crypto → crypto</b> : PAS d&apos;imposition (Loi de finances 2019)</li>
          <li><b>Crypto → stablecoin USDT/USDC</b> : reste discuté — en pratique déclaré comme exonéré, mais fragile</li>
          <li><b>Crypto → biens/services</b> : imposition sur la PV latente</li>
        </ul>
        <h3>Formulaire 2086</h3>
        <p>Obligation depuis 2019 : pour chaque cession crypto {">"} EUR, remplir le formulaire <b>2086</b>. Calcul : <br />
        <code>PV = (prix de cession) - [(portefeuille global à la cession) × (prix de cession / valeur totale du portefeuille)]</code></p>
        <p>Formule complexe — utilisez un outil (Waltio, Koinly) qui génère automatiquement le 2086 depuis vos exports Binance/Coinbase.</p>
        <h3>Cas particuliers</h3>
        <ul>
          <li><b>Airdrops</b> : imposés à la valeur au moment de la réception (BNC ou traitement PV variable)</li>
          <li><b>Staking / lending</b> : revenus imposables (BNC ou BIC selon l&apos;intensité)</li>
          <li><b>DeFi</b> : jurisprudence très floue en 2026 — documentez tout, prudence</li>
        </ul>
        <h3>Déclaration des comptes étrangers (formulaire 3916-bis)</h3>
        <p>Obligation absolue pour tout compte crypto ouvert à l&apos;étranger (Binance, Coinbase, Kraken US…). Amende = 750 € par compte non déclaré (jusqu&apos;à 10 000 € si valeur {">"} 50 000 €). <b>Ne pas oublier</b> — c&apos;est le point le plus contrôlé.</p>
        <p>Idem pour vos brokers CFD étrangers : formulaire <b>3916</b>.</p>
      </>
    ),
  },
  {
    slug: "trader-professionnel-vs-particulier",
    track: "fiscal",
    level: "Semi-pro",
    minutes: 6,
    order: 2,
    title: "Trader particulier vs professionnel : quand basculer ?",
    summary: "BNC pro ou BIC ? Les 5 critères et le calcul du seuil de rentabilité.",
    content: (
      <>
        <p>Si vous tradez sérieusement, l&apos;administration fiscale peut vous re-qualifier <b>professionnel</b>. C&apos;est très différent du régime particulier — et parfois avantageux.</p>
        <h3>Les 5 critères d&apos;habitude / professionnalisation</h3>
        <p>L&apos;URSSAF/fisc regarde :</p>
        <ol>
          <li><b>Volume et fréquence</b> — plusieurs trades par jour, plusieurs milliers par an</li>
          <li><b>Complexité des opérations</b> — dérivés, options complexes, arbitrages inter-marchés</li>
          <li><b>Ressources dédiées</b> — matériel professionnel, abonnements data payants, bureau dédié</li>
          <li><b>Revenus significatifs</b> — {">"} 30-50 k € / an de trading en revenu principal</li>
          <li><b>Absence d&apos;autre activité principale</b> — c&apos;est votre métier</li>
        </ol>
        <p>Un scalpeur qui fait 50 trades / jour, 200 j / an, tire 80% de ses revenus du trading = <b>quasi-certainement pro</b>. Un swing trader qui prend 3 positions / mois = <b>quasi-certainement particulier</b>.</p>
        <h3>Régime BNC (Bénéfices Non Commerciaux)</h3>
        <p>Régime le plus courant pour un trader pro solo :</p>
        <ul>
          <li>Impôts sur les bénéfices nets (recettes - charges déductibles)</li>
          <li>Cotisations sociales SSI (Sécurité Sociale des Indépendants) : 30-45% environ</li>
          <li>Charges déductibles : ordinateur, abonnements (Bloomberg, TradingView, ANATRADEX), coworking, formation, un smartphone…</li>
          <li>TVA en principe non applicable (opérations financières = exonérées)</li>
        </ul>
        <h3>Comparaison brutale</h3>
        <table className="text-sm text-white/80 my-3">
          <thead><tr className="text-white/60"><th>Cas</th><th>Particulier</th><th>Pro BNC</th></tr></thead>
          <tbody>
            <tr><td>PV brute 50k€</td><td className="text-white">50k</td><td className="text-white">50k</td></tr>
            <tr><td>Charges déductibles</td><td>0</td><td>-8k</td></tr>
            <tr><td>Base imposable</td><td>50k</td><td>42k</td></tr>
            <tr><td>Prélèvements sociaux</td><td>-17,2% (8600€)</td><td>-14k (SSI)</td></tr>
            <tr><td>Impôt</td><td>-12,8% (6400€)</td><td>variable (barème)</td></tr>
            <tr><td className="text-white">Net</td><td className="text-white">35k</td><td className="text-white">~22-26k</td></tr>
          </tbody>
        </table>
        <p><b>Contre-intuitif :</b> le régime pro est souvent MOINS avantageux fiscalement en France — sauf si vos charges déductibles sont énormes ou si vous voulez cotiser à la retraite. Consultez un fiscaliste.</p>
        <h3>Quand basculer volontairement ?</h3>
        <ul>
          <li>Vous voulez déduire des charges réelles importantes (bureau, matos, formations {">"} 10k€/an)</li>
          <li>Vous voulez cotiser retraite/santé (statut TNS)</li>
          <li>Vous voulez créer une <b>SASU</b> pour piloter votre imposition (dividendes vs salaire)</li>
        </ul>
        <h3>Attention : requalification d&apos;office</h3>
        <p>Si vous êtes en régime particulier mais que le fisc considère que vous êtes pro, il peut requalifier avec effet rétroactif (3 ans) + intérêts + pénalités. Anticipez si votre profil bascule.</p>
      </>
    ),
  },
  {
    slug: "structures-juridiques-sasu-eurl-holding",
    track: "fiscal",
    level: "Pro",
    minutes: 8,
    order: 3,
    title: "SASU, EURL, holding : structurer son activité de trader",
    summary: "Passé 100k€/an, la structuration devient rentable. Panorama des montages.",
    content: (
      <>
        <p>Au-delà de 100-150k€/an de PV nette, structurer son activité en société devient rentable. Panorama des montages les plus utilisés par les traders indépendants français.</p>
        <h3>SASU (Société par Actions Simplifiée Unipersonnelle)</h3>
        <p>Le plus flexible. IS (Impôt sur les Sociétés) à 15% jusqu&apos;à 42,5k€ de bénef, puis 25%. Vous pouvez :</p>
        <ul>
          <li>Vous verser un <b>salaire</b> (charges salariales 42%, patronales 43%, coût élevé)</li>
          <li>Vous verser des <b>dividendes</b> (flat tax 30%, plus efficient)</li>
          <li>Laisser les bénéfices en réserve pour investir (immobilier, autres sociétés, autres marchés)</li>
        </ul>
        <p>Stratégie classique : petit salaire minimum + dividendes à la fin de l&apos;année. Total charges/impôts ~30-38% vs 40-50% en BNC pro.</p>
        <h3>EURL (Entreprise Unipersonnelle à Responsabilité Limitée)</h3>
        <p>Alternative à la SASU. Par défaut, imposition IR (Impôt sur le Revenu). Option IS possible. Statut TNS (Travailleur Non Salarié) — cotisations SSI moins chères que salaire mais couverture moins bonne.</p>
        <p>Avantage : cotisations sociales plus basses (~40% du dividende vs 30% flat SASU). Désavantage : moins flexible, image moins pro pour signer avec brokers institutionnels.</p>
        <h3>Holding + société d&apos;exploitation (montage avancé)</h3>
        <p>Pour les gros patrimoines. Vous créez :</p>
        <ol>
          <li>Une <b>holding</b> (SASU ou SARL) qui détient les parts de votre trading company</li>
          <li>Une société d&apos;exploitation qui trade</li>
          <li>Les dividendes remontent en régime <em>mère-fille</em> (95% exonérés) à la holding</li>
          <li>La holding peut investir dans d&apos;autres activités (immo, autres deals) sans re-fiscaliser</li>
        </ol>
        <p>Économie fiscale : ~10-15 points par rapport à une SASU seule. Coût création + comptable : 2000-4000€/an supplémentaire.</p>
        <h3>Cas trader crypto — spécificités</h3>
        <p>Les cessions crypto en SASU sont taxées à l&apos;IS (25%) — bien mieux que le PFU 30% particulier. Mais attention : <b>l&apos;apport de crypto perso à la société est un fait générateur d&apos;imposition</b> (vous « vendez » vos cryptos à la société). Anticipez avec votre fiscaliste.</p>
        <h3>Le vrai piège : la sortie des fonds</h3>
        <p>L&apos;IS 25% + le PFU 30% sur les dividendes = imposition totale ~48% quand vous voulez sortir l&apos;argent. Une SASU n&apos;est intéressante que si vous <b>réinvestissez</b> le cash (immo, autres deals, laisser en trésorerie).</p>
        <h3>Optimisation avancée : PEA + assurance-vie</h3>
        <p>Vos gains société peuvent être versés en dividendes puis re-investis dans :</p>
        <ul>
          <li><b>PEA</b> — exonération PV après 5 ans (mais uniquement actions UE et ETF UE)</li>
          <li><b>Assurance-vie</b> — abattement 4600 €/an après 8 ans, transmission optimisée</li>
        </ul>
        <p><b>Ordre de grandeur :</b> pour un trader régulier 200k€/an net de PV, une structuration SASU + réinvestissement PEA/AV bien menée = économie fiscale de 15-25k€/an vs particulier. Le coût comptable/juridique = 3-5k€/an. <b>ROI très positif</b>, mais requiert une comptabilité rigoureuse.</p>
        <p><b>Recommandation :</b> à partir de 80k€/an de PV trading, prenez rendez-vous avec un expert-comptable spécialisé (attention : la plupart ne connaissent pas bien la fiscalité trading — cherchez-en un qui a plusieurs clients traders).</p>
      </>
    ),
  },
  {
    slug: "prop-firms-et-fiscalite",
    track: "fiscal",
    level: "Semi-pro",
    minutes: 5,
    order: 4,
    title: "Prop firms : quelle fiscalité en France ?",
    summary: "FTMO, MyForexFunds, Apex Trader. Reversal contract, LLC US, taxation.",
    content: (
      <>
        <p>Les <b>prop firms</b> (FTMO, MyForexFunds défunte, Apex Trader Funding, Fidelcrest, The5%ers…) ont explosé en popularité. Elles vous « financent » après un challenge et vous reversent 70-90% des gains. Mais quel est le régime fiscal en France ?</p>
        <h3>Structure typique d&apos;une prop firm</h3>
        <ul>
          <li>Vous payez un frais initial (100-1000€) pour un « challenge »</li>
          <li>Vous passez le challenge → contrat « funded trader »</li>
          <li>Vous tradez leur compte démo (jamais votre argent perso — attention à ne pas confondre)</li>
          <li>Vos gains sont versés par la prop firm (souvent basée à Dubaï, Chypre, USA)</li>
        </ul>
        <h3>Nature fiscale des revenus prop firm</h3>
        <p>Ce ne sont <b>PAS</b> des plus-values de trading (vous ne tradez pas votre argent). C&apos;est un <b>revenu de prestation de service</b> — vous rendez un service à la prop firm (trader leur capital) et êtes rémunéré.</p>
        <ul>
          <li>Régime : <b>BNC (Bénéfices Non Commerciaux)</b> ou <b>BIC</b> selon interprétation</li>
          <li>Micro-BNC accessible jusqu&apos;à 77 700€/an de recettes (abattement 34%)</li>
          <li>Au-delà : déclaration contrôlée (recettes - charges réelles)</li>
        </ul>
        <p><b>Attention</b> : ce n&apos;est PAS du PFU 30%. C&apos;est le <b>barème progressif de l&apos;IR</b> + cotisations SSI (30-45%). Total imposition peut atteindre 50-60% sur les tranches hautes.</p>
        <h3>Cas particulier : société étrangère + prêt</h3>
        <p>Certaines prop firms (FTMO récemment) structurent la relation comme un « prêt-consommation » ou une allocation de capital — d&apos;autres comme un contrat de sous-traitance. Le fisc français regarde la <em>substance</em>, pas la forme.</p>
        <p>Dans tous les cas : <b>déclarez</b>. Ne comptez pas sur le fait que la prop firm ne remonte aucune info au fisc français — les échanges automatisés d&apos;info (CRS, FATCA) fonctionnent et les banques françaises signalent les virements récurrents.</p>
        <h3>Optimisation possible</h3>
        <ul>
          <li>Créer une <b>micro-entreprise</b> avec le BNC comme activité principale (jusqu&apos;à 77,7k€/an)</li>
          <li>Créer une <b>SASU</b> qui « facture » la prop firm — vous facturez, la société encaisse, dividendes flat 30%</li>
        </ul>
        <h3>Attention aux arnaques</h3>
        <p>Beaucoup de prop firms ont fait faillite ou disparu (My Forex Funds, TrueFX Trader…). Ne comptez jamais un revenu prop firm avant qu&apos;il ne soit encaissé sur votre compte bancaire. Diversifiez les prop firms si vous en dépendez.</p>
        <p><b>Résumé pratique :</b> les revenus prop firm sont imposés comme revenus d&apos;activité (pas comme PV). C&apos;est plus lourd fiscalement que du trading perso. Anticipez avec un comptable, structurez-vous si les montants dépassent 30k€/an.</p>
      </>
    ),
  },
];

// Aggregate exports
export const EXTRA_TRACKS_V2_ARTICLES = [
  ...MACRO_ARTICLES,
  ...FISCAL_ARTICLES,
];

export const EXTRA_TRACKS_V2_META = [
  {
    id: "macro",
    label: "Macro / Fed & Cycles",
    emoji: "🌐",
    articles: MACRO_ARTICLES,
    intro: "5 articles sur la Fed, les cycles économiques, les indicateurs macro, la yield curve et le DXY. Le socle macro pour tout trader sérieux.",
    activeClass: "bg-emerald-500/20 border-emerald-500/50 text-emerald-300",
    introClass: "bg-emerald-500/5 border-emerald-500/20",
    introTitleClass: "text-emerald-300",
  },
  {
    id: "fiscal",
    label: "Fiscalité du trader (FR)",
    emoji: "🇫🇷",
    articles: FISCAL_ARTICLES,
    intro: "4 articles sur la fiscalité forex/crypto en France, particulier vs pro, SASU/EURL/holding, et prop firms. À jour 2026.",
    activeClass: "bg-blue-500/20 border-blue-500/50 text-blue-300",
    introClass: "bg-blue-500/5 border-blue-500/20",
    introTitleClass: "text-blue-300",
  },
];
